import React from "react";
import { Grid, Box, Typography, Paper, Divider, Skeleton, Chip, IconButton, Tooltip, LinearProgress, Avatar, AvatarGroup, useTheme } from "@mui/material";
import { tokens } from "../../../../theme";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import StreamDownloadLinks from "./StreamDownloadLinks";
import AssignedSiteVisitsWidget from "./SiteVisitWidget";
import { Venue } from "../../../site-visit/types/site-visit";
import { useEffect, useState } from "react";
import apiService from "../../../../service/Api";
import { BarChart, LineChart, ScatterChart } from "@mui/x-charts";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import NetworkCheckIcon from "@mui/icons-material/NetworkCheck";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import RefreshIcon from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import SpeedIcon from "@mui/icons-material/Speed";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

interface NetworkStat {
  provider: string;
  numberOfUses: number;
  averageSpeed: number;
}

interface UserVenueStat {
  user: string;
  venuesAdded: number;
  averageScore: number;
}

interface MonthlyData {
  month: string; // date string YYYY-MM-DD
  bookings: number;
  streams: number;
}

interface OperationsData {
  network_stats: NetworkStat[];
  user_venue_stats: UserVenueStat[];
  monthly_bookings: MonthlyData[];
  assigned_site_visits: AssignedSiteVisit[];
}

interface AssignedSiteVisit {
  id: number;
  status?: string;
  dueDate?: string | null;
  notes?: string | null;
  venue?: {
    id?: number | null;
    name?: string | null;
    type?: string | null;
    address?: string | null;
  } | null;
}

// Animated Metric Card Component
const AnimatedMetricCard: React.FC<{
  value: number;
  title: string;
  badge?: string;
  trend?: number;
  color: string;
  icon: React.ComponentType<Record<string, unknown>>;
}> = ({ value, title, badge, trend, color, icon: Icon }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 1000;
    const increment = value / (duration / 16);
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: "1px solid",
        borderColor: `${color}30`,
        position: "relative",
        overflow: "hidden",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 12px 24px ${color}20`,
          borderColor: color,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          right: 0,
          width: "100px",
          height: "100px",
          background: `radial-gradient(circle, ${color}15 0%, transparent 70%)`,
          borderRadius: "50%",
          transform: "translate(30%, -30%)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 4px 12px ${color}40`,
          }}
        >
          <Icon sx={{ color: "white", fontSize: 28 }} />
        </Box>
        {trend !== undefined && (
          <Chip
            icon={trend > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
            label={`${Math.abs(trend)}%`}
            size="small"
            sx={{
              backgroundColor: trend > 0 ? "#10b98120" : "#ef444420",
              color: trend > 0 ? "#10b981" : "#ef4444",
              fontWeight: 600,
              "& .MuiChip-icon": {
                color: "inherit",
              },
            }}
          />
        )}
      </Box>

      <Typography
        variant="h3"
        sx={{
          fontWeight: 700,
          mb: 0.5,
          color: color,
          fontSize: "2.5rem",
          lineHeight: 1,
        }}
      >
        {displayValue}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          fontWeight: 500,
          mb: 1.5,
        }}
      >
        {title}
      </Typography>

      {badge && (
        <Chip
          label={badge}
          size="small"
          sx={{
            backgroundColor: `${color}15`,
            color: color,
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      )}
    </Paper>
  );
};

// Enhanced Chart Container
const ChartContainer: React.FC<{
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  loading?: boolean;
  info?: string;
}> = ({ title, icon, children, loading = false, info }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 1000);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        p: 3,
        borderRadius: 3,
        backgroundColor: colors.primary[900],
        border: "1px solid",
        borderColor: "grey.200",
        transition: "all 0.3s ease",
        position: "relative",
        overflow: "hidden",
        "&:hover": {
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          borderColor: "primary.main",
          "& .chart-actions": {
            opacity: 1,
          },
        },
      }}
    >
      {refreshing && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
          }}
        />
      )}
      
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {icon && (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 8px rgba(25, 118, 210, 0.3)",
              }}
            >
              {React.cloneElement(
                icon as React.ReactElement<Record<string, unknown>>,
                { sx: { color: "white", fontSize: 20 } }
              )}
            </Box>
          )}
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: "1.1rem",
                color: "text.primary",
              }}
            >
              {title}
            </Typography>
            {info && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {info}
              </Typography>
            )}
          </Box>
        </Box>

        <Box className="chart-actions" sx={{ display: "flex", gap: 0.5, opacity: 0, transition: "opacity 0.3s" }}>
          <Tooltip title="Refresh data">
            <IconButton size="small" onClick={handleRefresh}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="More options">
            <IconButton size="small">
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Divider sx={{ mb: 2.5 }} />
      
      {loading ? (
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      ) : (
        children
      )}
    </Paper>
  );
};

// Stat Badge Component
const StatBadge: React.FC<{
  icon: React.ComponentType<Record<string, unknown>>;
  label: string;
  value: string | number;
  color: string;
}> = ({ icon: Icon, label, value, color }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 2,
      py: 1,
      borderRadius: 2,
      backgroundColor: `${color}10`,
      border: "1px solid",
      borderColor: `${color}30`,
      transition: "all 0.3s ease",
      "&:hover": {
        transform: "scale(1.05)",
        boxShadow: `0 4px 8px ${color}20`,
      },
    }}
  >
    <Icon sx={{ fontSize: 18, color }} />
    <Box>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", lineHeight: 1 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const OperationsDashboard: React.FC = () => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const user = useSelector((state: RootState) => state.auth.user);

  const [visits, setVisits] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [operationsData, setOperationsData] = useState<OperationsData | null>(null);

  useEffect(() => {
    const fetchOperationsData = async () => {
      if (!user?.id) return;
      try {
        setLoading(true);
        const response = await apiService.get<OperationsData>(
          "api/management/dashboard/operations/"
        );

        setOperationsData(response.data);

        const allowedStatuses: Venue["status"][] = [
          "pending",
          "assigned",
          "awaiting_review",
          "approved",
          "rejected",
        ];
        const mapped: Venue[] = (response.data.assigned_site_visits || []).map(
          (item: AssignedSiteVisit) => ({
            id: item.venue?.id ?? item.id,
            name: item.venue?.name ?? "Unknown Venue",
            type: item.venue?.type ?? "",
            address: item.venue?.address ?? "",
            status: allowedStatuses.includes(item.status as Venue["status"])
              ? (item.status as Venue["status"])
              : "pending",
            assignee: user.first_name + " " + user.last_name,
            dueDate: item.dueDate ?? undefined,
            notes: item.notes ?? "",
          })
        );

        setVisits(mapped);
      } catch (err) {
        console.error("Failed to fetch operations data:", err);
        setOperationsData({
          network_stats: [],
          user_venue_stats: [],
          monthly_bookings: [],
          assigned_site_visits: [],
        });
        setVisits([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOperationsData();
  }, [user?.first_name, user?.id, user?.last_name]);

  const networkStats = operationsData?.network_stats || [];
  const userVenueStats = operationsData?.user_venue_stats || [];
  const monthlyData = operationsData?.monthly_bookings || [];

  const primaryColor = colors.primary[500];
  const secondaryColor = colors.primary[300];
  const accentColor = "#10b981";
  const warningColor = "#f59e0b";
  const purpleColor = "#8b5cf6";
  const pinkColor = "#ec4899";

  // Calculate metrics
  const totalBookings = monthlyData.reduce((sum, d) => sum + d.bookings, 0);
  const avgBookings = totalBookings > 0 ? (totalBookings / monthlyData.length).toFixed(0) : "0";
  const bookingTrendNum = monthlyData.length >= 2 
    ? ((monthlyData[monthlyData.length - 1].bookings - monthlyData[0].bookings) / (monthlyData[0].bookings || 1) * 100)
    : 0;
  const bookingTrend = bookingTrendNum.toFixed(0);

  const topPerformer = userVenueStats.length > 0
    ? userVenueStats.reduce((max, user) => user.venuesAdded > max.venuesAdded ? user : max)
    : { user: "N/A", venuesAdded: 0, averageScore: 0 };

  const avgNetworkSpeed = networkStats.length > 0
    ? (networkStats.reduce((sum, s) => sum + s.averageSpeed, 0) / networkStats.length).toFixed(1)
    : "0";

  const totalVenuesAdded = userVenueStats.reduce((sum, s) => sum + s.venuesAdded, 0);

  return (
    <Box
      sx={{
        flexGrow: 1,
        background: `linear-gradient(180deg, ${colors.primary[800]} 0%, ${colors.primary[800]} 100%)`,
        minHeight: "100vh",
        pb: 4,
        border: `1px solid ${colors.primary[300]}30`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        borderRadius: 2,
      }}
    >
      <Grid container spacing={3}>
        {/* Enhanced Metrics Cards */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <AnimatedMetricCard
            value={10}
            title="Pending Vimeo Links"
            badge="4 overdue"
            trend={-12}
            color={warningColor}
            icon={SignalCellularAltIcon}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <AnimatedMetricCard
            value={10}
            title="Published Vimeo Links"
            badge="6 this week"
            trend={25}
            color={accentColor}
            icon={SignalCellularAltIcon}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <AnimatedMetricCard
            value={totalBookings}
            title="Total Bookings"
            badge={`${avgBookings} avg/month`}
            trend={bookingTrendNum}
            color={purpleColor}
            icon={CalendarMonthIcon}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <AnimatedMetricCard
            value={totalVenuesAdded}
            title="Total Venues Added"
            badge={`${topPerformer.user} leads`}
            trend={18}
            color={pinkColor}
            icon={EmojiEventsIcon}
          />
        </Grid>

        {/* Main Content Area */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Grid container spacing={3}>
            {/* Monthly Bookings with Enhanced Visualization */}
            <Grid size={{ xs: 12 }}>
              <ChartContainer
                title="Monthly Bookings & Streams Trend"
                icon={<CalendarMonthIcon />}
                loading={loading}
                info="Last 12 months performance"
              >
                <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <StatBadge
                    icon={CalendarMonthIcon}
                    label="Peak Month"
                    value={monthlyData.length > 0
                      ? `${monthlyData.reduce((max, d) => d.bookings > max.bookings ? d : max, monthlyData[0]).month} (${monthlyData.reduce((max, d) => d.bookings > max.bookings ? d : max, monthlyData[0]).bookings})`
                      : "N/A"}
                    color={primaryColor}
                  />
                  <StatBadge
                    icon={TrendingUpIcon}
                    label="Growth Rate"
                    value={`${bookingTrendNum > 0 ? '+' : ''}${bookingTrend}%`}
                    color={accentColor}
                  />
                  <StatBadge
                    icon={SpeedIcon}
                    label="Avg Bookings"
                    value={avgBookings}
                    color={secondaryColor}
                  />
                </Box>

                <LineChart
                  xAxis={[
                    {
                      scaleType: "point",
                      data: monthlyData.map((d) => d.month),
                      tickLabelStyle: {
                        fontSize: 12,
                        angle: 0,
                      },
                    },
                  ]}
                  series={[
                    {
                      data: monthlyData.map((d) => d.bookings),
                      label: "Bookings",
                      color: primaryColor,
                      curve: "catmullRom",
                      showMark: true,
                      area: true,
                    },
                    {
                      data: monthlyData.map((d) => d.streams),
                      label: "Streams",
                      color: accentColor,
                      curve: "catmullRom",
                      showMark: true,
                      area: false,
                    },
                  ]}
                  height={350}
                  margin={{ bottom: 40 }}
                  yAxis={[{ label: "Count" }]}
                  grid={{ vertical: false, horizontal: true }}
                  sx={{
                    "& .MuiLineElement-root": {
                      strokeWidth: 3,
                    },
                    "& .MuiAreaElement-root": {
                      fillOpacity: 0.1,
                    },
                    "& .MuiChartsAxis-tickLabel": {
                      fontSize: "12px !important",
                    },
                  }}
                />
              </ChartContainer>
            </Grid>

            {/* Network Provider Detailed Analysis */}
            <Grid size={{ xs: 12, md: 6 }}>
              <ChartContainer
                title="Network Provider Comparison"
                icon={<NetworkCheckIcon />}
                loading={loading}
                info="Usage vs Speed Analysis"
              >
                <BarChart
                  xAxis={[
                    {
                      scaleType: "band",
                      data: networkStats.map((s) => s.provider),
                      tickLabelStyle: {
                        fontSize: 12,
                      },
                    },
                  ]}
                  series={[
                    {
                      data: networkStats.map((s) => s.numberOfUses),
                      label: "Number of Uses",
                      color: primaryColor,
                    },
                    {
                      data: networkStats.map((s) => s.averageSpeed),
                      label: "Avg Speed (Mbps)",
                      color: accentColor,
                    },
                  ]}
                  height={320}
                  yAxis={[{ label: "Value" }]}
                  grid={{ horizontal: true }}
                />
              </ChartContainer>
            </Grid>

            {/* User Performance Scatter */}
            <Grid size={{ xs: 12, md: 6 }}>
              <ChartContainer
                title="User Venue Performance Matrix"
                icon={<TrendingUpIcon />}
                loading={loading}
                info="Quantity vs Quality Analysis"
              >
                <ScatterChart
                  series={[
                    {
                      data: userVenueStats.map((s) => ({
                        x: s.venuesAdded,
                        y: s.averageScore,
                        id: s.user,
                      })),
                      label: "Users",
                      color: primaryColor,
                      valueFormatter: (value) =>
                        value ? `${value.id}: ${value.x} venues, ${value.y}★` : "",
                    },
                  ]}
                  xAxis={[
                    {
                      label: "Venues Added",
                      min: 0,
                      max: Math.max(...userVenueStats.map((s) => s.venuesAdded), 30) + 5,
                    },
                  ]}
                  yAxis={[
                    {
                      label: "Average Score (★)",
                      min: 0,
                      max: 5,
                    },
                  ]}
                  height={320}
                  grid={{ vertical: true, horizontal: true }}
                />
              </ChartContainer>
            </Grid>

            {/* User Contributions */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <ChartContainer
                title="Top Contributors"
                icon={<PeopleAltIcon />}
                loading={loading}
                info="Venue additions by user"
              >
                <Box sx={{ mb: 2 }}>
                  <AvatarGroup max={4} sx={{ justifyContent: "flex-start" }}>
                    {userVenueStats.map((user, idx) => (
                      <Tooltip key={idx} title={`${user.user} - ${user.venuesAdded} venues`}>
                        <Avatar
                          sx={{
                            bgcolor: `hsl(${idx * 60}, 70%, 60%)`,
                            fontWeight: 600,
                            cursor: "pointer",
                            transition: "transform 0.3s",
                            "&:hover": {
                              transform: "scale(1.2)",
                              zIndex: 10,
                            },
                          }}
                        >
                          {user.user.split(" ").map(n => n[0]).join("")}
                        </Avatar>
                      </Tooltip>
                    ))}
                  </AvatarGroup>
                </Box>
                
                <BarChart
                  xAxis={[
                    {
                      scaleType: "band",
                      data: userVenueStats.map((s) => s.user),
                      tickLabelStyle: {
                        angle: -45,
                        textAnchor: "end",
                        fontSize: 11,
                      },
                    },
                  ]}
                  series={[
                    {
                      data: userVenueStats.map((s) => s.venuesAdded),
                      label: "Venues Added",
                      color: secondaryColor,
                    },
                  ]}
                  height={280}
                  margin={{ bottom: 80 }}
                  grid={{ horizontal: true }}
                />
              </ChartContainer>
            </Grid>

            {/* User Quality Scores */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <ChartContainer
                title="Quality Score Trends"
                icon={<EmojiEventsIcon />}
                loading={loading}
                info="Average venue ratings by user"
              >
                <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <StatBadge
                    icon={EmojiEventsIcon}
                    label="Highest Score"
                    value={`${topPerformer.user} (${topPerformer.averageScore}★)`}
                    color={accentColor}
                  />
                </Box>
                
                <LineChart
                  xAxis={[
                    {
                      scaleType: "point",
                      data: userVenueStats.map((s) => s.user),
                      tickLabelStyle: {
                        angle: -45,
                        textAnchor: "end",
                        fontSize: 11,
                      },
                    },
                  ]}
                  series={[
                    {
                      data: userVenueStats.map((s) => s.averageScore),
                      label: "Average Score",
                      color: accentColor,
                      curve: "catmullRom",
                      showMark: true,
                      area: true,
                    },
                  ]}
                  height={280}
                  margin={{ bottom: 80 }}
                  yAxis={[
                    {
                      min: 0,
                      max: 5,
                      label: "Score (★)",
                    },
                  ]}
                  grid={{ horizontal: true }}
                  sx={{
                    "& .MuiAreaElement-root": {
                      fillOpacity: 0.15,
                    },
                  }}
                />
              </ChartContainer>
            </Grid>
          </Grid>
        </Grid>

        {/* Sidebar - Network Stats Overview */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Box sx={{ position: "sticky", top: 20 }}>
            
            <AssignedSiteVisitsWidget
              userName={user?.first_name || "User"}
              assignedVisits={visits}
              isLoading={loading}
              onViewAll={() => console.log("Navigate to full site visits")}
            />

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                mb: 3,
                background: `linear-gradient(135deg, ${primaryColor}20 0%, ${primaryColor}05 100%)`,
                border: "1px solid",
                borderColor: `${primaryColor}30`,
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: `0 8px 24px ${primaryColor}20`,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 4px 8px ${primaryColor}30`,
                  }}
                >
                  <NetworkCheckIcon sx={{ color: "white", fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Network Overview
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Average Network Speed
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: primaryColor, mb: 1 }}>
                  {avgNetworkSpeed} Mbps
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min((parseFloat(avgNetworkSpeed) / 60) * 100, 100)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: `${primaryColor}20`,
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 4,
                      background: `linear-gradient(90deg, ${primaryColor} 0%, ${accentColor} 100%)`,
                    },
                  }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {networkStats.map((stat, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: colors.primary[900],
                      border: "1px solid",
                      borderColor: "grey.200",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateX(4px)",
                        borderColor: primaryColor,
                        boxShadow: `0 4px 12px ${primaryColor}20`,
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {stat.provider}
                      </Typography>
                      <Chip
                        label={`${stat.averageSpeed} Mbps`}
                        size="small"
                        sx={{
                          backgroundColor: `${accentColor}15`,
                          color: accentColor,
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min((stat.numberOfUses / 150) * 100, 100)}
                        sx={{
                          flexGrow: 1,
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: `${primaryColor}15`,
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 3,
                            backgroundColor: primaryColor,
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {stat.numberOfUses} uses
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        </Grid>

        {/* Stream Download Links - Full Width */}
        <Grid size={{ xs: 12 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "grey.200",
              overflow: "hidden",
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              },
            }}
          >
            <StreamDownloadLinks />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OperationsDashboard;